import { useEffect } from 'react'

import ArrowLeftIcon from '@heroicons/react/outline/esm/ArrowLeftIcon.js'
import ChevronRightIcon from '@heroicons/react/outline/esm/ChevronRightIcon.js'
import DocumentTextIcon from '@heroicons/react/outline/esm/DocumentTextIcon.js'

import {
  PostListFilterByEnum,
  PostListFilterByOperator,
  PostStatus,
  PostTypeContext,
} from '@tribeplatform/gql-client/types'
import { EntityImage } from '@tribeplatform/react-components/common/components'
import {
  useLogin,
  useQuery,
} from '@tribeplatform/react-components/common/hooks'
import { getPostIdFromPath } from '@tribeplatform/react-components/common/utils'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { PostForm } from '@tribeplatform/react-components/PostForm'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useAuthMember,
  useNetwork,
  usePost,
  usePosts,
  useSpace,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { BC } from '../../types/index.js'
import { Drafts } from './Drafts.js'
import {
  getTranslatedUpdatePostText,
  getTranslatedCreatePostText,
  getTranslatedPostTypeName,
} from './translations.js'
import { ComposerProps } from './types.js'

export const ComposerBlock: BC<ComposerProps> = ({
  new: isNew,
  spacePath,
  postPath,
  postTypeSlug,
  ...rest
}) => {
  const intl = useI18n()
  const { Link, push, replace, location } = useRouter()
  const { view, space_id: spaceId } = useQuery() || {}

  const isSpaceSet = !!spacePath || !!spaceId

  const { data: space } = useSpace({
    fields: {
      authMemberProps: {
        availablePostTypes: 'all',
        spaceMember: {
          role: 'basic',
        },
      },
      image: 'all',
    },
    variables: spaceId ? { id: spaceId } : { path: spacePath },
    useQueryOptions: { enabled: isSpaceSet },
  })

  const postId = postPath ? getPostIdFromPath(postPath) : null
  const { data: post, isFetched } = usePost({
    variables: { id: postId },
    useQueryOptions: { enabled: !!postId },
  })

  const { data: member, isGuest } = useAuthMember()
  const { showLogin } = useLogin()

  const {
    data: { postTypes: networkPostTypes },
  } = useNetwork()

  const postType = networkPostTypes.find(
    postType => postType.id === (postTypeSlug || post?.postTypeId),
  )

  const { data: draftPosts } = usePosts({
    variables: {
      postTypeIds: postType?.id ? [postType?.id] : undefined,
      limit: 0,
      filterBy: [
        {
          keyString: 'status',
          key: PostListFilterByEnum.publishedAt,
          operator: PostListFilterByOperator.equals,
          value: JSON.stringify('DRAFTED'),
        },
        {
          keyString: 'createdById',
          key: PostListFilterByEnum.publishedAt,
          operator: PostListFilterByOperator.equals,
          value: JSON.stringify(member?.id),
        },
      ],
    },
    fields: 'basic',
    useInfiniteQueryOptions: {
      enabled: isNew,
    },
  })
  const draftsCount = draftPosts?.pages?.[0]?.totalCount

  let availablePostTypes = []

  if (isSpaceSet) {
    availablePostTypes = space?.authMemberProps?.availablePostTypes?.filter(
      postType => postType?.context === PostTypeContext.post,
    )
  } else {
    availablePostTypes = networkPostTypes?.filter(
      postType =>
        postType?.context === PostTypeContext.post &&
        postType?.slug !== 'tribe-message' &&
        postType?.authMemberProps?.availableSpaces?.length > 0,
    )
  }

  const defaultTargetSpace = postType?.authMemberProps?.availableSpaces?.[0]

  const onCancel = () => {
    if (location?.key) {
      window?.history?.back()
    } else {
      push(isNew ? space.relativeUrl || '/' : post?.relativeUrl)
    }
  }

  useEffect(() => {
    if (isGuest) {
      showLogin(true)
    }
  }, [isGuest, showLogin])

  if (isGuest) {
    return null
  }

  if (view === 'drafts') {
    return <Drafts postTypeId={postType?.id} />
  }

  if (isNew && !postTypeSlug) {
    if (availablePostTypes?.length === 1) {
      replace(
        `${isSpaceSet ? `/${space?.slug}` : ''}/new?post_type=${
          availablePostTypes[0].id
        }`,
      )
      return null
    }
    return (
      <Card {...rest}>
        <Card.Header
          title={
            <div className="flex space-s-3 items-center">
              <Button
                variant="basic"
                rounded
                size="lg"
                leadingIcon={<ArrowLeftIcon className="rtl:-scale-x-100" />}
                onClick={onCancel}
              ></Button>
              <span className="text-xl flex-1">
                <T
                  id="Blocks.Composer.CreateButtonText"
                  defaultMessage="Create a new"
                />
              </span>
              {draftsCount > 0 && (
                <Button
                  variant="basic"
                  rounded
                  as={Link}
                  href="?view=drafts"
                  leadingIcon={<DocumentTextIcon />}
                  className="px-5"
                  size="lg"
                >
                  <T
                    id="Generics.Drafts.Plural"
                    defaultMessage="{drafts_count, plural, =0 {No drafts} =1 {# draft} other {# drafts}}"
                    values={{ drafts_count: draftsCount }}
                  />
                </Button>
              )}
            </div>
          }
        />
        <Card.Content>
          <List divider spacing="none" className="border-t border-line-subdued">
            {availablePostTypes?.map(postType => (
              <List.Item key={postType?.id}>
                <Link
                  variant="inherit"
                  className="hover:bg-surface-hovered px-4 py-5 sm:p-6 flex space-s-3 items-center"
                  href={`${isSpaceSet ? `/${space?.slug}` : ''}/new?post_type=${
                    postType.id
                  }`}
                >
                  <EntityImage
                    size="xs"
                    entity={{ image: postType.icon, name: postType?.name }}
                  />
                  <span className="flex-1">
                    {getTranslatedPostTypeName(intl, postType.name)}
                  </span>
                  <ChevronRightIcon className="w-6 h-6 rtl:-scale-x-100" />
                </Link>
              </List.Item>
            ))}
          </List>
        </Card.Content>
      </Card>
    )
  }
  if (!isNew && !post) {
    return null
  }

  const postTypeTitle = post?.id
    ? getTranslatedUpdatePostText(intl, postType?.name)
    : getTranslatedCreatePostText(intl, postType?.name)

  return (
    <Card {...rest}>
      <Card.Header
        title={
          <div className="flex space-s-3 items-center">
            <Button
              variant="basic"
              rounded
              size="lg"
              leadingIcon={<ArrowLeftIcon className="rtl:-scale-x-100" />}
              onClick={onCancel}
            />
            <span className="text-xl flex-1">{postTypeTitle}</span>
            {draftsCount > 0 && (
              <Button
                variant="basic"
                rounded
                as={Link}
                href={`?view=drafts${
                  postType?.id ? `&post_type=${postType?.id}` : ''
                }`}
                leadingIcon={<DocumentTextIcon />}
                className="px-5"
                size="lg"
              >
                <T
                  id="Generics.Drafts.Plural"
                  defaultMessage="{drafts_count, plural, =0 {No drafts} =1 {# draft} other {# drafts}}"
                  values={{ drafts_count: draftsCount }}
                />
              </Button>
            )}
          </div>
        }
      />
      <Card.Content>
        <PostForm
          postType={postType}
          space={isSpaceSet ? space : defaultTargetSpace}
          availableSpaces={postType?.authMemberProps?.availableSpaces}
          post={post}
          isPostReady={isFetched}
          onCancel={onCancel}
          onSubmit={post => {
            if (post?.status !== PostStatus.DRAFTED) {
              replace(post?.relativeUrl)
            } else {
              replace(`${post?.relativeUrl}/edit`)
            }
          }}
        />
      </Card.Content>
    </Card>
  )
}
